// Campaign Utility Functions
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { CAMPAIGN_STATUS, isCampaignActive, isCampaignExpired, getCampaignDaysRemaining } from '../data/campaignModels';

/**
 * Check Campaign Expiration and Auto-Update Status
 */
export const checkCampaignExpiration = (campaign) => {
  if (!campaign) {
    return { isExpired: true, isValid: false, daysRemaining: null };
  }

  if (campaign.status === CAMPAIGN_STATUS.EXPIRED) {
    return { isExpired: true, isValid: false, daysRemaining: 0 };
  }

  if (!campaign.endDate) {
    return { isExpired: false, isValid: true, daysRemaining: null }; // No expiration
  }

  let endDate = null;
  try {
    if (campaign.endDate instanceof Date) {
      endDate = campaign.endDate;
    } else if (campaign.endDate.toDate && typeof campaign.endDate.toDate === 'function') {
      endDate = campaign.endDate.toDate();
    } else if (campaign.endDate.seconds) {
      endDate = new Date(campaign.endDate.seconds * 1000);
    } else if (typeof campaign.endDate === 'string' || typeof campaign.endDate === 'number') {
      endDate = new Date(campaign.endDate);
    }

    if (!endDate || isNaN(endDate.getTime())) {
      return { isExpired: false, isValid: true, daysRemaining: null };
    }
  } catch (error) {
    console.error('Error parsing endDate:', error);
    return { isExpired: false, isValid: true, daysRemaining: null };
  }

  const now = new Date();
  const isExpired = endDate < now;

  if (isExpired) {
    return { isExpired: true, isValid: false, daysRemaining: 0 };
  }

  const diffTime = endDate - now;
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return { isExpired: false, isValid: true, daysRemaining };
};

/**
 * Validate and Update Campaign Expiration Status
 */
export const validateAndUpdateCampaign = async (campaign, campaignRef = null) => {
  if (!campaign) return null;

  const check = checkCampaignExpiration(campaign);
  
  if (check.isExpired && campaign.status === CAMPAIGN_STATUS.ACTIVE && campaignRef) {
    try {
      await updateDoc(campaignRef, {
        status: CAMPAIGN_STATUS.EXPIRED,
        updatedAt: Timestamp.now()
      });
      
      return {
        ...campaign,
        status: CAMPAIGN_STATUS.EXPIRED
      };
    } catch (error) {
      console.error('Error updating campaign status:', error);
    }
  }

  return campaign;
};

/**
 * Get Active Campaigns for a Position
 */
/**
 * Normalize language code (e.g., 'en-US' -> 'en', 'th-TH' -> 'th')
 * @param {string} lang - Language code from i18n
 * @returns {string} Normalized language code
 */
export const normalizeLanguageCode = (lang) => {
  if (!lang) return 'th';
  
  // Handle special cases first
  if (lang.startsWith('zh-CN') || lang === 'zh-CN') return 'zh-CN';
  if (lang.startsWith('zh-TW') || lang === 'zh-TW') return 'zh-TW';
  
  // Extract base language (e.g., 'en-US' -> 'en', 'th-TH' -> 'th')
  const baseLang = lang.split('-')[0];
  
  // Map to supported languages
  const supportedLangs = ['th', 'en', 'de', 'fr', 'ru', 'zh-CN', 'zh-TW', 'ko', 'ja', 'ar'];
  if (supportedLangs.includes(baseLang)) {
    return baseLang;
  }
  
  // Default to 'th' if not supported
  return 'th';
};

export const getActiveCampaignsForPosition = async (positionId, language = 'th') => {
  // Normalize language code
  const normalizedLanguage = normalizeLanguageCode(language);
  try {
    const campaignsRef = collection(db, 'campaigns');
    const now = Timestamp.now();
    
    // Query for approved and active campaigns
    // Note: Firestore doesn't support complex queries easily, so we'll query and filter in code
    // We need: status = approved OR active, isActive !== false, position enabled
    const q = query(
      campaignsRef,
      where('status', 'in', [CAMPAIGN_STATUS.APPROVED, CAMPAIGN_STATUS.ACTIVE]),
      limit(100) // Get more to filter
    );

    const snapshot = await getDocs(q);
    const campaigns = [];

    for (const docSnap of snapshot.docs) {
      const campaign = { id: docSnap.id, ref: docSnap.ref, ...docSnap.data() };
      
      // Check if campaign is active (isActive !== false)
      if (campaign.isActive === false) {
        console.log(`[CampaignFilter] Campaign ${campaign.id} skipped: isActive = false`);
        continue; // Skip inactive campaigns
      }
      
      // Check if campaign has this position enabled
      const positionConfig = campaign.positions?.find(p => p.positionId === positionId);
      if (!positionConfig || !positionConfig.enabled) {
        console.log(`[CampaignFilter] Campaign ${campaign.id} skipped: position ${positionId} not enabled`, {
          positions: campaign.positions,
          positionConfig
        });
        continue; // Skip campaigns without this position enabled
      }
      
      // Check expiration
      const expirationCheck = checkCampaignExpiration(campaign);
      
      if (expirationCheck.isExpired) {
        console.log(`[CampaignFilter] Campaign ${campaign.id} skipped: expired`);
        // Auto-update expired status
        await validateAndUpdateCampaign(campaign, docSnap.ref);
        continue; // Skip expired campaigns
      }

      // Check if campaign is within date range
      // For approved campaigns, check date range manually
      const now = new Date();
      let startDate = null;
      let endDate = null;
      
      try {
        if (campaign.startDate) {
          if (campaign.startDate.toDate && typeof campaign.startDate.toDate === 'function') {
            startDate = campaign.startDate.toDate();
          } else if (campaign.startDate.seconds) {
            startDate = new Date(campaign.startDate.seconds * 1000);
          } else {
            startDate = new Date(campaign.startDate);
          }
        }
        
        if (campaign.endDate) {
          if (campaign.endDate.toDate && typeof campaign.endDate.toDate === 'function') {
            endDate = campaign.endDate.toDate();
          } else if (campaign.endDate.seconds) {
            endDate = new Date(campaign.endDate.seconds * 1000);
          } else {
            endDate = new Date(campaign.endDate);
          }
        }
      } catch (error) {
        console.error('Error parsing campaign dates:', error);
      }
      
      // Check if campaign is within date range
      // Allow campaigns to start on the same day (compare dates, not exact time)
      if (startDate) {
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (nowDateOnly < startDateOnly) {
          console.log(`[CampaignFilter] Campaign ${campaign.id} skipped: not started yet (startDate: ${startDate}, now: ${now})`);
          continue; // Campaign hasn't started yet
        }
      }
      if (endDate) {
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (nowDateOnly > endDateOnly) {
          console.log(`[CampaignFilter] Campaign ${campaign.id} skipped: ended (endDate: ${endDate}, now: ${now})`);
          continue; // Campaign has ended
        }
      }

      // Check targeting (language, roles, etc.)
      if (campaign.targeting?.languages && !campaign.targeting.languages.includes(normalizedLanguage)) {
        console.log(`[CampaignFilter] Campaign ${campaign.id} skipped: language mismatch (targeting: ${campaign.targeting.languages}, current: ${normalizedLanguage}, original: ${language})`);
        continue;
      }

      console.log(`[CampaignFilter] Campaign ${campaign.id} passed all filters - adding to list`);
      campaigns.push(campaign);
    }
    
    console.log(`[CampaignFilter] Total campaigns after filtering: ${campaigns.length}`, campaigns.map(c => ({ id: c.id, name: c.name?.th || c.name?.en })));

    // Sort by priority
    campaigns.sort((a, b) => {
      const aPriority = a.positions?.find(p => p.positionId === positionId)?.priority || 999;
      const bPriority = b.positions?.find(p => p.positionId === positionId)?.priority || 999;
      return aPriority - bPriority;
    });

    return campaigns;
  } catch (error) {
    console.error('Error fetching active campaigns:', error);
    return [];
  }
};

/**
 * Track Campaign View
 */
export const trackCampaignView = async (campaignId, userId = null) => {
  try {
    const campaignRef = doc(db, 'campaigns', campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) return;

    const campaign = campaignSnap.data();
    const now = new Date();
    const today = now.toISOString().split('T')[0]; // YYYY-MM-DD

    // Update total views
    await updateDoc(campaignRef, {
      'analytics.views': (campaign.analytics?.views || 0) + 1,
      'analytics.uniqueViews': userId ? (campaign.analytics?.uniqueViews || 0) + 1 : campaign.analytics?.uniqueViews || 0,
      updatedAt: Timestamp.now()
    });

    // Update daily stats
    const dailyStats = campaign.analytics?.dailyStats || [];
    const todayStats = dailyStats.find(stat => stat.date === today);
    
    if (todayStats) {
      // Update existing daily stat
      const updatedStats = dailyStats.map(stat => {
        if (stat.date === today) {
          return {
            ...stat,
            views: stat.views + 1,
            uniqueViews: userId ? (stat.uniqueViews || 0) + 1 : stat.uniqueViews || 0
          };
        }
        return stat;
      });
      
      await updateDoc(campaignRef, {
        'analytics.dailyStats': updatedStats
      });
    } else {
      // Create new daily stat
      await updateDoc(campaignRef, {
        'analytics.dailyStats': [
          ...dailyStats,
          {
            date: today,
            views: 1,
            clicks: 0,
            ctr: 0,
            uniqueViews: userId ? 1 : 0,
            uniqueClicks: 0
          }
        ]
      });
    }
  } catch (error) {
    console.error('Error tracking campaign view:', error);
  }
};

/**
 * Track Campaign Click
 */
export const trackCampaignClick = async (campaignId, userId = null) => {
  try {
    const campaignRef = doc(db, 'campaigns', campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) return;

    const campaign = campaignSnap.data();
    const now = new Date();
    const today = now.toISOString().split('T')[0]; // YYYY-MM-DD

    // Update total clicks
    const totalViews = campaign.analytics?.views || 0;
    const totalClicks = (campaign.analytics?.clicks || 0) + 1;
    const ctr = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;

    await updateDoc(campaignRef, {
      'analytics.clicks': totalClicks,
      'analytics.ctr': ctr,
      'analytics.uniqueClicks': userId ? (campaign.analytics?.uniqueClicks || 0) + 1 : campaign.analytics?.uniqueClicks || 0,
      updatedAt: Timestamp.now()
    });

    // Update daily stats
    const dailyStats = campaign.analytics?.dailyStats || [];
    const todayStats = dailyStats.find(stat => stat.date === today);
    
    if (todayStats) {
      // Update existing daily stat
      const updatedStats = dailyStats.map(stat => {
        if (stat.date === today) {
          const dailyViews = stat.views || 0;
          const dailyClicks = (stat.clicks || 0) + 1;
          const dailyCtr = dailyViews > 0 ? (dailyClicks / dailyViews) * 100 : 0;
          
          return {
            ...stat,
            clicks: dailyClicks,
            ctr: dailyCtr,
            uniqueClicks: userId ? (stat.uniqueClicks || 0) + 1 : stat.uniqueClicks || 0
          };
        }
        return stat;
      });
      
      await updateDoc(campaignRef, {
        'analytics.dailyStats': updatedStats
      });
    } else {
      // Create new daily stat
      await updateDoc(campaignRef, {
        'analytics.dailyStats': [
          ...dailyStats,
          {
            date: today,
            views: 0,
            clicks: 1,
            ctr: 0,
            uniqueViews: 0,
            uniqueClicks: userId ? 1 : 0
          }
        ]
      });
    }
  } catch (error) {
    console.error('Error tracking campaign click:', error);
  }
};

/**
 * Check if User has Claimed Campaign
 */
export const hasUserClaimedCampaign = async (campaignId, userId) => {
  try {
    const claimsRef = collection(db, 'campaignClaims');
    const q = query(
      claimsRef,
      where('campaignId', '==', campaignId),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking campaign claim:', error);
    return false;
  }
};

export default {
  checkCampaignExpiration,
  validateAndUpdateCampaign,
  getActiveCampaignsForPosition,
  trackCampaignView,
  trackCampaignClick,
  hasUserClaimedCampaign
};

